import { Drawer, createStyles, Menu } from "@mantine/core";
import moment from "moment";
import { useState } from "react";
import { GetBillingERADetais } from "../../services";
import { getStediClaimStatus } from "../../services/billing";
import { showError } from "../../utils";
import StediClaimStatusModal from "./StediClaimStatusModal";

const INK          = "#171717";
const INK2         = "#262626";
const MUTED        = "#667085";
const LINE         = "#E5E5E5";
const LINE_SOFT    = "#F1F1F1";
const SURFACE_SOFT = "#FAFAFA";
const SURFACE_ALT  = "#F4F4F5";
const ACCENT       = "#FFBA45";
const ACCENT_HOVER = "#EEAA33";
const GREEN        = "#15803D";
const GREEN_SOFT   = "#F0FDF4";
const GREEN_BORDER = "#BBF7D0";
const WARN         = "#B45309";
const WARN_SOFT    = "#FFFBEB";
const WARN_BORDER  = "#FCD34D";

const useStyles = createStyles(() => ({
  root: {
    ".mantine-Drawer-body": { padding: 0, height: "100vh" },
    ".mantine-Drawer-header": { display: "none" },
    ".mantine-Drawer-content": { background: "#fff" },
  },
  shell: { display: "flex", flexDirection: "column", height: "100vh" },

  /* head */
  head: {
    padding: "22px 26px",
    borderBottom: `1px solid ${LINE_SOFT}`,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    flexShrink: 0,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    margin: 0,
  },
  title: { margin: "4px 0 0", fontSize: 22, fontWeight: 700, color: INK, letterSpacing: "-0.018em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: MUTED },
  closeBtn: {
    width: 36,
    height: 36,
    background: SURFACE_ALT,
    border: 0,
    borderRadius: 8,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    color: INK2,
    flexShrink: 0,
    "&:hover": { background: "#E4E4E7" },
    "& svg": { width: 18, height: 18 },
  },

  body: { flex: 1, overflowY: "auto", padding: "22px 26px" },

  foot: {
    padding: "14px 26px 18px",
    borderTop: `1px solid ${LINE_SOFT}`,
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexShrink: 0,
  },

  /* ── ERA (paid) specific ── */
  eraHero: {
    background: `linear-gradient(135deg, ${GREEN_SOFT} 0%, #ECFDF5 100%)`,
    border: `1px solid ${GREEN_BORDER}`,
    borderRadius: 14,
    padding: "18px 20px",
    marginBottom: 22,
  },
  eraHeroLabel: { fontSize: 11, color: GREEN, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" },
  eraHeroAmt: { fontSize: 30, fontWeight: 800, color: GREEN, letterSpacing: "-0.02em", marginTop: 4, fontVariantNumeric: "tabular-nums" },
  eraHeroMeta: { fontSize: 12.5, color: "#047857", marginTop: 6 },

  amountGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 },
  amountItem: {
    background: SURFACE_SOFT,
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    padding: "12px 10px",
    textAlign: "center",
  },
  amountItemPaid: {
    background: GREEN_SOFT,
    borderColor: GREEN_BORDER,
    "& $amountVal": { color: GREEN },
  },
  amountKey: { fontSize: 10, color: MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" },
  amountVal: { fontSize: 15, color: INK, fontWeight: 700, marginTop: 6, fontVariantNumeric: "tabular-nums" },
  amountValPaid: { color: GREEN },

  adjRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 14px",
    border: `1px solid ${WARN_BORDER}`,
    background: WARN_SOFT,
    borderRadius: 10,
    marginBottom: 8,
    "&:last-child": { marginBottom: 0 },
  },
  adjCodePill: {
    display: "inline-block",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: 11,
    fontWeight: 700,
    background: ACCENT,
    color: INK,
    padding: "2px 7px",
    borderRadius: 4,
  },
  adjMeta: { fontSize: 12.5, color: WARN, fontWeight: 600, marginTop: 6 },
  adjDesc: { fontSize: 11.5, color: "#7B5A00", marginTop: 4, lineHeight: 1.45 },
  adjAmt: { fontWeight: 800, color: WARN, fontVariantNumeric: "tabular-nums", fontSize: 14, whiteSpace: "nowrap" },

  downloadBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: 0,
    background: ACCENT,
    color: INK,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    "& svg": { width: 16, height: 16 },
    "&:hover": { background: ACCENT_HOVER },
    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
  },

  /* ── Acknowledged specific (same amber palette as pending) ── */
  ackHero: {
    background: `linear-gradient(135deg, ${WARN_SOFT} 0%, #FFF8E1 100%)`,
    border: `1px solid ${WARN_BORDER}`,
    borderRadius: 14,
    padding: "18px 20px",
    marginBottom: 22,
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
  },
  ackHeroIco: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "#fff",
    border: `1px solid ${WARN_BORDER}`,
    display: "grid",
    placeItems: "center",
    color: WARN,
    flexShrink: 0,
    "& svg": { width: 20, height: 20 },
  },
  ackHeroLabel: { fontSize: 11, color: WARN, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" },
  ackHeroTitle: { margin: "4px 0 0", fontSize: 18, fontWeight: 700, color: "#7B5A00", letterSpacing: "-0.012em" },
  ackHeroText: { margin: "6px 0 0", fontSize: 13, color: "#7B5A00", lineHeight: 1.5 },

  /* ── Pending (processing) specific ── */
  procHero: {
    background: `linear-gradient(135deg, ${WARN_SOFT} 0%, #FFF8E1 100%)`,
    border: `1px solid ${WARN_BORDER}`,
    borderRadius: 14,
    padding: "18px 20px",
    marginBottom: 22,
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
  },
  procHeroIco: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "#fff",
    border: `1px solid ${WARN_BORDER}`,
    display: "grid",
    placeItems: "center",
    color: WARN,
    flexShrink: 0,
    "& svg": { width: 20, height: 20 },
  },
  procHeroLabel: { fontSize: 11, color: WARN, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" },
  procHeroTitle: { margin: "4px 0 0", fontSize: 18, fontWeight: 700, color: "#7B5A00", letterSpacing: "-0.012em" },
  procHeroText: { margin: "6px 0 0", fontSize: 13, color: "#7B5A00", lineHeight: 1.5 },

  ghostBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: `1px solid ${LINE}`,
    background: "#fff",
    color: INK2,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    "& svg": { width: 16, height: 16 },
    "&:hover": { background: SURFACE_ALT },
  },
  checkBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: 0,
    background: ACCENT,
    color: INK,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    "& svg": { width: 16, height: 16 },
    "&:hover": { background: ACCENT_HOVER },
  },

  /* shared */
  section: { marginBottom: 26, "&:last-child": { marginBottom: 0 } },
  sectionLabel: {
    margin: "0 0 12px",
    fontSize: 11,
    fontWeight: 700,
    color: MUTED,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px 24px",
    background: SURFACE_SOFT,
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    padding: "16px 18px",
  },
  metaKey: { fontSize: 11, color: MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" },
  metaVal: { fontSize: 13.5, color: INK, fontWeight: 600, marginTop: 4, wordBreak: "break-word" },
  svcTable: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 12.5,
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    overflow: "hidden",
    "& th": {
      textAlign: "left",
      padding: "10px 12px",
      fontSize: 10.5,
      fontWeight: 700,
      color: MUTED,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      borderBottom: `1px solid ${LINE}`,
      background: SURFACE_SOFT,
    },
    "& td": { padding: 12, borderBottom: `1px solid ${LINE_SOFT}` },
    "& tbody tr:last-child td": { borderBottom: 0 },
    "& th.r, & td.r": { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  },
  codeMono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: 12,
    fontWeight: 700,
    color: INK,
    background: "#F4F4F5",
    padding: "2px 6px",
    borderRadius: 4,
  },
  units: { fontSize: 11.5, color: MUTED, marginTop: 4 },
}));

/* ─── helpers ── */
const num = (v) => parseFloat(v) || 0;
const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? moment(d).format("MMM DD, YYYY") : "—");
const fmtDateTime = (d) => (d ? moment(d).format("MMM DD, YYYY · hh:mm A") : "—");
const claimTotal = (c) =>
  c?.total_charge != null && c?.total_charge !== ""
    ? num(c.total_charge)
    : (c?.charge || []).reduce((t, ch) => t + num(ch?.charge), 0);
const memberId = (c) => c?.ins_number || c?.pat_id || c?.payer_id || c?.subscriber_id || "—";

const ERADetailsDrawer = ({ opened, onClose, claim, batch, payerName, providerName }) => {
  const { classes, cx } = useStyles();
  const [downloading, setDownloading] = useState(false);
  const [stediModalOpen, setStediModalOpen] = useState(false);

  const handleCheckStediStatus = async () => {
    if (!batch?._id) return;
    setStediModalOpen(true);
    await getStediClaimStatus(batch._id);
  };

  if (!claim) return null;

  const isPaid         = claim?.status === "P";
  const isAcknowledged = claim?.status === "A";
  const total  = claimTotal(claim);
  // const allowed = num(claim?.allowed_amount) || total;
  // const paid    = num(claim?.paid_amount) || (isPaid ? total : 0);
  // const adjustments = total - allowed;
  const paid    = num(claim?.billedAmount) || num(claim?.paid_amount) || (isPaid ? total : 0);
  const allowed = paid;
  const adjustments = 0;

  const claimId = claim?.claimmd_id || "—";
  const patientName = [claim?.pat_name_f, claim?.pat_name_l].filter(Boolean).join(" ") || "—";
  const eft = claim?.eft_number || claim?.trace_number || "—";
  const paymentDate = claim?.payment_date || batch?.updatedAt || batch?.createdAt;
  const submittedDate = batch?.createdAt;
  const serviceDate = (claim?.charge || []).find((c) => c?.from_date)?.from_date;
  const lines = claim?.charge || [];

  const geteraDetails = async (type) => {
    if (!claim?.claimmd_id) return;
    try {
      setDownloading(true);
      await new Promise((resolve) => setTimeout(resolve, 0));
      const res = await GetBillingERADetais({ type, claimmid: claim.claimmd_id });
      const eraDetails = res?.data?.billingEradetails;
      if (!eraDetails || eraDetails?.error) {
        showError(eraDetails?.error || "ERA details not available for this claim.");
        return;
      }
      let blob, fileName;
      if (type === "pdf") {
        const content = eraDetails.data?.[0];
        const byteCharacters = atob(content);
        const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0));
        blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
        fileName = `ERA_${claim.claimmd_id}.pdf`;
      } else if (type === "era") {
        blob = new Blob([eraDetails.data?.[0]], { type: "text/plain" });
        fileName = `ERA_${claim.claimmd_id}.txt`;
      } else {
        const mimeType = type === "json" ? "application/json" : "text/xml";
        blob = new Blob([JSON.stringify(eraDetails, null, 2)], { type: mimeType });
        fileName = `ERA_${claim.claimmd_id}.${type}`;
      }
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      showError("Failed to download ERA. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (<>
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={540}
      padding={0}
      zIndex={1001}
      withCloseButton={false}
      className={classes.root}
      overlayOpacity={0.45}
      overlayColor="#0F172A"
      transitionDuration={180}
    >
      <div className={classes.shell}>

        {/* ── head ── */}
        <div className={classes.head}>
          <div>
            <p className={classes.eyebrow}>{isPaid ? "ERA Details" : "Claim Status"}</p>
            <h2 className={classes.title}>{claimId}</h2>
            <p className={classes.sub}>{patientName} · {payerName || claim?.payer_name || "—"}</p>
          </div>
          <button type="button" className={classes.closeBtn} onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── body ── */}
        <div className={classes.body}>

          {isPaid ? (
            /* ===== PAID / ERA view ===== */
            <>
              {/* green hero */}
              <div className={classes.eraHero}>
                <div className={classes.eraHeroLabel}>Total paid</div>
                <div className={classes.eraHeroAmt}>{fmtMoney(paid)}</div>
                <div className={classes.eraHeroMeta}>
                  Paid in full · EFT {eft} · {fmtDate(paymentDate)}
                </div>
              </div>

              {/* amount breakdown */}
              <div className={classes.section}>
                <h6 className={classes.sectionLabel}>Amount Breakdown</h6>
                <div className={classes.amountGrid}>
                  <div className={classes.amountItem}>
                    <div className={classes.amountKey}>Billed</div>
                    <div className={classes.amountVal}>{fmtMoney(total)}</div>
                  </div>
                  <div className={classes.amountItem}>
                    <div className={classes.amountKey}>Allowed</div>
                    <div className={classes.amountVal}>{fmtMoney(allowed)}</div>
                  </div>
                  <div className={classes.amountItem}>
                    <div className={classes.amountKey}>Adjustments</div>
                    <div className={classes.amountVal}>
                      {adjustments > 0 ? `−${fmtMoney(adjustments)}` : fmtMoney(0)}
                    </div>
                  </div>
                  <div className={cx(classes.amountItem, classes.amountItemPaid)}>
                    <div className={classes.amountKey}>Paid</div>
                    <div className={cx(classes.amountVal, classes.amountValPaid)}>{fmtMoney(paid)}</div>
                  </div>
                </div>
              </div>

              {/* remittance */}
              <div className={classes.section}>
                <h6 className={classes.sectionLabel}>Remittance</h6>
                <div className={classes.metaGrid}>
                  <div>
                    <div className={classes.metaKey}>EFT / Trace #</div>
                    <div className={classes.metaVal}>{eft}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Payment date</div>
                    <div className={classes.metaVal}>{fmtDate(paymentDate)}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Payer</div>
                    <div className={classes.metaVal}>{payerName || claim?.payer_name || "—"}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Payer Claim ID</div>
                    <div className={classes.metaVal}>{claimId}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Method</div>
                    <div className={classes.metaVal}>ACH</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Status</div>
                    <div className={classes.metaVal} style={{ color: GREEN }}>Paid in full</div>
                  </div>
                </div>
              </div>

              {/* subscriber & provider */}
              <div className={classes.section}>
                <h6 className={classes.sectionLabel}>Subscriber &amp; Provider</h6>
                <div className={classes.metaGrid}>
                  <div>
                    <div className={classes.metaKey}>Patient</div>
                    <div className={classes.metaVal}>{patientName}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Member ID</div>
                    <div className={classes.metaVal}>{memberId(claim)}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Service date</div>
                    <div className={classes.metaVal}>{fmtDate(serviceDate)}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Rendering provider</div>
                    <div className={classes.metaVal}>{providerName || "—"}</div>
                  </div>
                </div>
              </div>

              {/* service lines */}
              <div className={classes.section}>
                <h6 className={classes.sectionLabel}>Service Lines</h6>
                <table className={classes.svcTable}>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th className="r">Billed</th>
                      <th className="r">Allowed</th>
                      <th className="r">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length ? lines.map((ch, i) => {
                      const billed = num(ch?.charge);
                      return (
                        <tr key={i}>
                          <td>
                            <span className={classes.codeMono}>{ch?.proc_code || ch?.procedure_code || "—"}</span>
                            <div className={classes.units}>
                              {ch?.units ? `${ch.units} units` : ""}
                              {ch?.from_date ? ` · ${moment(ch.from_date).format("MMM DD")}` : ""}
                            </div>
                          </td>
                          <td className="r">{fmtMoney(billed)}</td>
                          <td className="r">{fmtMoney(billed)}</td>
                          <td className="r"><b>{fmtMoney(billed)}</b></td>
                        </tr>
                      );
                    }) : <tr><td colSpan={4}>—</td></tr>}
                  </tbody>
                </table>
              </div>

              {/* adjustments */}
              {adjustments > 0 && (
                <div className={classes.section}>
                  <h6 className={classes.sectionLabel}>Adjustments (CARC / RARC)</h6>
                  <div className={classes.adjRow}>
                    <div>
                      <span className={classes.adjCodePill}>CO-45</span>
                      <div className={classes.adjMeta}>Charge exceeds fee schedule / maximum allowable</div>
                      <div className={classes.adjDesc}>Group: Contractual Obligation · Reason code 45</div>
                    </div>
                    <div className={classes.adjAmt}>−{fmtMoney(adjustments)}</div>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* ===== PENDING / ACKNOWLEDGED view ===== */
            <>
              {/* hero: blue for acknowledged, amber for pending */}
              {isAcknowledged ? (
                <div className={classes.ackHero}>
                  <div className={classes.ackHeroIco}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <div>
                    <div className={classes.ackHeroLabel}>Acknowledged by payer</div>
                    <h3 className={classes.ackHeroTitle}>Claim acknowledged</h3>
                    <p className={classes.ackHeroText}>
                      {payerName || claim?.payer_name || "Payer"} has received and acknowledged this claim on {fmtDate(submittedDate)}.
                      Awaiting adjudication. Typical turnaround is 7–14 days.
                    </p>
                  </div>
                </div>
              ) : (
                <div className={classes.procHero}>
                  <div className={classes.procHeroIco}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                  </div>
                  <div>
                    <div className={classes.procHeroLabel}>Awaiting payer</div>
                    <h3 className={classes.procHeroTitle}>Still processing</h3>
                    <p className={classes.procHeroText}>
                      Submitted to {payerName || claim?.payer_name || "payer"} on {fmtDate(submittedDate)}.
                      Typical turnaround is 10–14 days. We auto-poll the clearinghouse hourly.
                    </p>
                  </div>
                </div>
              )}

              {/* submission */}
              <div className={classes.section}>
                <h6 className={classes.sectionLabel}>Submission</h6>
                <div className={classes.metaGrid}>
                  <div>
                    <div className={classes.metaKey}>Claim ID</div>
                    <div className={classes.metaVal}>{claimId}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Clearinghouse ID</div>
                    <div className={classes.metaVal}>{claim?.clearinghouse_id || claim?.claimmd_id || "—"}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Submitted</div>
                    <div className={classes.metaVal}>{fmtDateTime(submittedDate)}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Last poll</div>
                    <div className={classes.metaVal}>{fmtDateTime(batch?.updatedAt || submittedDate)}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Service date</div>
                    <div className={classes.metaVal}>{fmtDate(serviceDate)}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Total billed</div>
                    <div className={classes.metaVal}>{fmtMoney(total)}</div>
                  </div>
                </div>
              </div>

              {/* patient & provider */}
              <div className={classes.section}>
                <h6 className={classes.sectionLabel}>Patient &amp; Provider</h6>
                <div className={classes.metaGrid}>
                  <div>
                    <div className={classes.metaKey}>Patient</div>
                    <div className={classes.metaVal}>{patientName}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Member ID</div>
                    <div className={classes.metaVal}>{memberId(claim)}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Payer</div>
                    <div className={classes.metaVal}>{payerName || claim?.payer_name || "—"}</div>
                  </div>
                  <div>
                    <div className={classes.metaKey}>Rendering provider</div>
                    <div className={classes.metaVal}>{providerName || "—"}</div>
                  </div>
                </div>
              </div>

              {/* service lines submitted */}
              <div className={classes.section}>
                <h6 className={classes.sectionLabel}>Service Lines Submitted</h6>
                <table className={classes.svcTable}>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th className="r">Units</th>
                      <th className="r">Charges</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length ? lines.map((ch, i) => (
                      <tr key={i}>
                        <td>
                          <span className={classes.codeMono}>{ch?.proc_code || ch?.procedure_code || "—"}</span>
                          <div className={classes.units}>
                            {ch?.from_date ? `${moment(ch.from_date).format("MMM DD")}` : ""}
                            {ch?.mod1 ? ` · Mod ${ch.mod1}` : ""}
                          </div>
                        </td>
                        <td className="r">{ch?.units || "—"}</td>
                        <td className="r"><b>{fmtMoney(num(ch?.charge))}</b></td>
                      </tr>
                    )) : <tr><td colSpan={3}>—</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}

        </div>

        {/* ── foot ── */}
        <div className={classes.foot}>
          {isPaid ? (
            <Menu shadow="md" width={160} position="top-end">
              <Menu.Target>
                <button
                  type="button"
                  className={classes.downloadBtn}
                  disabled={downloading || !claim?.claimmd_id}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <path d="M7 10l5 5 5-5" />
                    <path d="M12 15V3" />
                  </svg>
                  {downloading ? "Loading…" : "Download ERA"}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, marginLeft: 2 }}>
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => geteraDetails("era")}>ERA Format (.txt)</Menu.Item>
                <Menu.Item onClick={() => geteraDetails("pdf")}>PDF</Menu.Item>
                <Menu.Item onClick={() => geteraDetails("xml/json")}>XML / JSON</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          ) : (
            <>
              <button type="button" className={classes.ghostBtn}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z" />
                </svg>
                Edit
              </button>
              {/* <button type="button" className={classes.checkBtn} onClick={handleCheckStediStatus}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
                Check Status
              </button> */}
            </>
          )}
        </div>

      </div>
    </Drawer>

    <StediClaimStatusModal
      opened={stediModalOpen}
      onClose={() => setStediModalOpen(false)}
    />
  </> );
};

export default ERADetailsDrawer;
